import { UUID } from "@bitory-gmbh/nidavellir";
import { isImmutable } from "@/services/backend/documents/lib/document-state";
import { Unlink } from "lucide-react";
import t from "@/lang/lang";
import {
  ETable,
  ETableBody,
  ETableCell,
  ETableCellDatePicker,
  ETableCellInput,
  ETableCellSelect,
  ETableHead,
  ETableHeader,
  ETableRow,
} from "@/shared/components/tables/e-table";
import {
  updateHtzReportCache,
  PatchMicrobialAssessmentRequest,
  useHtzReportPatchMicrobialAssessmentMutation,
  useHtzReportShowQuery,
  useHtzReportUpdateMicrobialAssessmentSampleMutation,
} from "@/services/backend/htz/report/api/report-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { MicrobialAssessment } from "@/services/backend/htz/inspection/types/microbial-assessment";
import {
  patchMicrobialAssessment,
  Report,
} from "@/services/backend/htz/report/types/report";
import {
  Result,
  ResultItems,
} from "@/services/backend/htz/inspection/components/inspection-result";
import { Kind } from "@/services/backend/samples/types/kind";
import {
  updateAirMicrobialSampleSetCache,
  useSamplesAirMicrobialSampleSetPutMutation,
  useSamplesAirMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/air-microbial-api";
import { useAirMicrobialReference } from "@/services/backend/samples/components/air-microbial-reference-field";
import { MoldDifferentiationDialog } from "@/services/backend/samples/components/mold-differentiation-dialog";
import {
  updateSurfaceMicrobialSampleSetCache,
  useSamplesSurfaceMicrobialSampleSetPutMutation,
  useSamplesSurfaceMicrobialSampleSetShowQuery,
} from "@/services/backend/samples/api/surface-microbial-api";
import {
  updateDustDensitySampleCache,
  useSamplesDustDensitySamplePutMutation,
  useSamplesDustDensitySampleShowQuery,
} from "@/services/backend/samples/api/dust-density-sample-api";
import {
  updateWaterSampleCache,
  useSamplesWaterSamplePutMutation,
  useSamplesWaterSampleShowQuery,
} from "@/services/backend/samples/api/water-sample-api";
import {
  Measurement,
  updateMeasurement,
} from "@/services/backend/samples/types/water-sample";
import { MicrobialAssessmentDetailsDialog } from "@/services/backend/htz/report/components/microbial-assessment-details-dialog";
import {
  NumberInputField,
  TextInputField,
} from "@/shared/components/field/input-field";
import {
  AirMicrobialSampleSet,
  updateAirMicrobialSetNumber,
} from "@/services/backend/samples/types/air-microbial-sample";
import { updateSurfaceMicrobialSetNumber } from "@/services/backend/samples/types/surface-microbial-sample";
import { updateSampleNumber } from "@/services/backend/samples/types/sample";
import { InputDebounced } from "@/shared/components/inputs/input-debounced";
import { LaboratorySelect } from "@/services/backend/samples/components/laboratory-select";
import { AhuComponentSelect } from "@/services/backend/htz/ahu/components/ahu-component-select";
import { ReportAssessmentRemoveDialog } from "@/services/backend/htz/report/components/report-assessment-remove-dialog";
import { ReportMicrobialSampleLinkDialog } from "@/services/backend/htz/report/components/report-microbial-sample-link-dialog";
import {
  countFieldErrors,
  joinFieldName,
  useErrValidationFailedContext,
} from "@/shared/lib/errors/err-validation-failed-context";
import { Button } from "@/shared/components/ui/button";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";

// The table is one grid for every kind of sample, so every row spans the
// same twelve columns: component, sample number, laboratory, six value
// columns, the date, the result and the row actions.
const COLUMNS = 12;

export function MicrobialAssessmentsTable({ report }: { report: Report }) {
  const { data } = useHtzReportShowQuery({ id: report.id });

  if (!data) return;

  // Every assessment is shown, grouped by the kind of its sample. An
  // assessment without a sample belongs to no group, which is why it gets
  // one of its own - it is the state an assessment starts in.
  const byKind = (kind: Kind) =>
    data.microbialAssessments.filter((a) => a.kind === kind);
  const unassigned = data.microbialAssessments.filter(
    (a) =>
      a.kind !== Kind.AirMicrobial &&
      a.kind !== Kind.SurfaceMicrobial &&
      a.kind !== Kind.DustDensity &&
      a.kind !== Kind.Water,
  );

  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Komponente")}</ETableHead>
          <ETableHead>{t("Probennummer")}</ETableHead>
          <ETableHead>{t("Labor")}</ETableHead>
          <ETableHead colSpan={6}>{t("Werte")}</ETableHead>
          <ETableHead>{t("Durchgeführt am")}</ETableHead>
          <ETableHead>{t("Ergebnis")}</ETableHead>
          <ETableHead />
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {data.microbialAssessments.length === 0 && (
          <TableEmptyRow colSpan={COLUMNS} />
        )}

        {unassigned.length > 0 && (
          <>
            <ETableRow>
              <ETableCell
                className="bg-secondary h-8 font-medium"
                colSpan={COLUMNS}
              >
                {t("Nicht zugeordnet")}
              </ETableCell>
            </ETableRow>
            {unassigned.map((assessment) => (
              <UnassignedRow
                key={assessment.id}
                reportId={data.id}
                assessmentId={assessment.id}
              />
            ))}
          </>
        )}

        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Luftkeimproben")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Einheit")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Caso")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Malz")}</ETableCell>
          <ETableCell className="bg-secondary">{t("DG18")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Gesamt")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Schimmelpilze")}</ETableCell>
          <ETableCell className="bg-secondary" colSpan={3}></ETableCell>
        </ETableRow>
        {byKind(Kind.AirMicrobial).map((assessment) => (
          <AirMicrobialSampleRow
            key={assessment.id}
            reportId={data.id}
            assessment={assessment}
          />
        ))}

        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Oberflächenkeimproben")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Einheit")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Caso")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Malz")}</ETableCell>
          <ETableCell className="bg-secondary">{t("DG18")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Gesamt")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Schimmelpilze")}</ETableCell>
          <ETableCell className="bg-secondary" colSpan={3}></ETableCell>
        </ETableRow>
        {byKind(Kind.SurfaceMicrobial).map((assessment) => (
          <SurfaceMicrobialSampleRow
            key={assessment.id}
            reportId={data.id}
            assessment={assessment}
          />
        ))}

        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Staubdichtemessungen")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Einheit")}</ETableCell>
          <ETableCell className="bg-secondary">{t("Konzentration")}</ETableCell>
          <ETableCell className="bg-secondary" colSpan={7}></ETableCell>
        </ETableRow>
        {byKind(Kind.DustDensity).map((assessment) => (
          <DustDensitySampleRow
            key={assessment.id}
            reportId={data.id}
            assessment={assessment}
          />
        ))}

        <ETableRow>
          <ETableCell className="bg-secondary h-8 font-medium" colSpan={3}>
            {t("Wasserproben")}
          </ETableCell>
          <ETableCell className="bg-secondary">{t("Desinfiziert")}</ETableCell>
          <ETableCell className="bg-secondary">
            {t("Temperatur (°C)")}
          </ETableCell>
          <ETableCell className="bg-secondary" colSpan={4}>
            {t("Zählungen")}
          </ETableCell>
          <ETableCell className="bg-secondary" colSpan={3}></ETableCell>
        </ETableRow>
        {byKind(Kind.Water).map((assessment) => (
          <WaterSampleRow
            key={assessment.id}
            reportId={data.id}
            assessment={assessment}
          />
        ))}
      </ETableBody>
    </ETable>
  );
}

/**
 * useAssessmentRow wires the cells every row shares: the component, the
 * date and the result of the assessment itself, all saved on the report.
 */
function useAssessmentRow(reportId: UUID, assessmentId: UUID) {
  const { data } = useHtzReportShowQuery({ id: reportId });
  const { fieldErrors } = useErrValidationFailedContext();
  const fieldName = joinFieldName("microbialAssessments", assessmentId);

  const request: PatchMicrobialAssessmentRequest = {
    id: reportId,
    assessmentId,
  };
  const [set] = useOptimisticDebouncedMutation(
    request,
    updateHtzReportCache,
    useHtzReportPatchMicrobialAssessmentMutation(),
  );

  const assessment = data?.microbialAssessments.find(
    (a) => a.id === assessmentId,
  );

  return {
    report: data,
    assessment,
    disabled: !data || isImmutable(data),
    fieldNameFor: (field: string) => joinFieldName(fieldName, field),
    hasErrors: (field: string) =>
      countFieldErrors(fieldErrors, joinFieldName(fieldName, field)) > 0,
    setComponentId: (componentId: UUID) => {
      if (!data || !assessment) return;
      set(
        { ...request, componentId },
        patchMicrobialAssessment(data, { ...assessment, componentId }),
      );
    },
    setConductedAt: (conductedAt: string) => {
      if (!data || !assessment) return;
      set(
        { ...request, conductedAt },
        patchMicrobialAssessment(data, { ...assessment, conductedAt }),
      );
    },
    // The patch carries no recommendation: the backend derives it from
    // the result, and the report is refetched afterwards.
    setResult: (result: Result) => {
      if (!data || !assessment) return;
      set(
        { ...request, result },
        patchMicrobialAssessment(data, { ...assessment, result }),
      );
    },
    setRecommendation: (recommendation: string) => {
      if (!data || !assessment) return;
      set(
        { ...request, recommendation },
        patchMicrobialAssessment(data, { ...assessment, recommendation }),
      );
    },
  };
}

/**
 * RowActions unlinks the sample of an assessment and removes the
 * assessment. The sample itself is never deleted here: it may be part of a
 * delivery to the laboratory or used by another document.
 */
function RowActions({
  reportId,
  assessmentId,
  hasSample,
  disabled,
}: {
  reportId: UUID;
  assessmentId: UUID;
  hasSample: boolean;
  disabled?: boolean;
}) {
  const [unlink, { isLoading }] =
    useHtzReportUpdateMicrobialAssessmentSampleMutation();

  const onUnlink = () => {
    if (isLoading) {
      return;
    }

    // A zero kind clears the link on the backend.
    unlink({ id: reportId, assessmentId, sampleKind: "", sampleId: null })
      .unwrap()
      .catch(() => {
        // the error is surfaced by the report page
      });
  };

  if (disabled) {
    return null;
  }

  return (
    <div className="flex items-center justify-end gap-1">
      {hasSample && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onUnlink}
          disabled={isLoading}
          title={t("Probe entfernen")}
        >
          <Unlink />
        </Button>
      )}
      <ReportAssessmentRemoveDialog
        reportId={reportId}
        assessmentId={assessmentId}
        title={t("Beurteilung wirklich löschen?")}
        description={t(
          "Die Beurteilung wird aus dem Anlagenbericht gelöscht. Die zugeordnete Probe bleibt erhalten und kann an anderer Stelle weiterverwendet werden.",
        )}
      />
    </div>
  );
}

/**
 * UnassignedRow is a microbial assessment without a sample. It is the
 * state an assessment starts in, so the row offers to link one.
 */
function UnassignedRow({
  reportId,
  assessmentId,
}: {
  reportId: UUID;
  assessmentId: UUID;
}) {
  const row = useAssessmentRow(reportId, assessmentId);

  if (!row.report || !row.assessment) return;

  return (
    <ETableRow>
      <ETableCell className="px-0">
        <AhuComponentSelect
          ahuDataId={row.report.ahuDataId!}
          componentId={row.assessment.componentId}
          onComponentIdChange={row.setComponentId}
          fieldName={row.fieldNameFor("componentId")}
          disabled={row.disabled}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCell colSpan={8}>
        <div className="flex items-center gap-2">
          <span
            className={
              row.hasErrors("sampleId") || row.hasErrors("kind")
                ? "text-sm text-red-600"
                : "text-muted-foreground text-sm"
            }
          >
            {t("Keine Probe zugeordnet")}
          </span>
          {!row.disabled && (
            <ReportMicrobialSampleLinkDialog
              reportId={reportId}
              assessmentId={assessmentId}
            />
          )}
        </div>
      </ETableCell>
      <ETableCellDatePicker
        date={row.assessment.conductedAt}
        onDateChange={row.setConductedAt}
        disabled={row.disabled}
        fieldName={row.fieldNameFor("conductedAt")}
      />
      <ETableCellSelect
        value={row.assessment.result}
        onValueChange={(result) => row.setResult(result as Result)}
        label={t("Ergebnis")}
        items={ResultItems}
        disabled={row.disabled}
        fieldName={row.fieldNameFor("result")}
      />
      <ETableCell className="px-0 text-right">
        <RowActions
          reportId={reportId}
          assessmentId={assessmentId}
          hasSample={false}
          disabled={row.disabled}
        />
      </ETableCell>
    </ETableRow>
  );
}

function AirMicrobialSampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const row = useAssessmentRow(reportId, assessment.id);

  const { data: sample } = useSamplesAirMicrobialSampleSetShowQuery(
    { id: assessment.sampleId! },
    { skip: !assessment.sampleId },
  );

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateAirMicrobialSampleSetCache,
    useSamplesAirMicrobialSampleSetPutMutation(),
    // The sample is an entity of its own and its cells show no errors, so
    // its errors must not end up among the errors of the report.
    { writeToErrValidationFailedContext: false },
  );

  if (!row.report || !row.assessment || !sample) return;

  const disabled = row.disabled;

  return (
    <>
      <ETableRow>
        <ETableCell className="px-0">
          <AhuComponentSelect
            ahuDataId={row.report.ahuDataId!}
            componentId={row.assessment.componentId}
            onComponentIdChange={row.setComponentId}
            fieldName={row.fieldNameFor("componentId")}
            disabled={disabled}
            className="rounded-none border-none shadow-none"
          />
        </ETableCell>
        <ETableCellInput
          value={sample.number}
          onChange={(e) =>
            setSample(updateAirMicrobialSetNumber(sample, e.target.value))
          }
          disabled={disabled}
        />
        <ETableCell className="p-0">
          <LaboratorySelect
            labId={sample.labId}
            onLabIdChange={(labId) => setSample({ ...sample, labId })}
            className="rounded-none border-none shadow-none"
            disabled={disabled}
          />
        </ETableCell>
        <ETableCellInput
          value={sample.unit}
          onChange={(e) => setSample({ ...sample, unit: e.target.value })}
          disabled={disabled}
        />
        <ETableCellInput
          value={sample.caso?.value}
          onChange={(e) =>
            setSample({
              ...sample,
              caso: { ...sample.caso!, value: e.target.value },
            })
          }
          disabled={disabled}
        />
        <ETableCellInput
          value={sample.malt?.value}
          onChange={(e) =>
            setSample({
              ...sample,
              malt: { ...sample.malt!, value: e.target.value },
            })
          }
          disabled={disabled}
        />
        <ETableCellInput
          value={sample.dg18?.value}
          onChange={(e) =>
            setSample({
              ...sample,
              dg18: { ...sample.dg18!, value: e.target.value },
            })
          }
          disabled={disabled}
        />
        <ETableCellInput
          value={sample.value}
          onChange={(e) => setSample({ ...sample, value: e.target.value })}
          disabled={disabled}
        />
        <ETableCell className="p-0">
          <MoldDifferentiationDialog
            moldDifferentiation={sample.moldDifferentiation}
            onMoldDifferentiationChange={(moldDifferentiation) =>
              setSample({ ...sample, moldDifferentiation })
            }
            disabled={disabled}
          />
        </ETableCell>
        <ETableCellDatePicker
          date={row.assessment.conductedAt}
          onDateChange={row.setConductedAt}
          disabled={disabled}
          fieldName={row.fieldNameFor("conductedAt")}
        />
        <ETableCellSelect
          value={row.assessment.result}
          onValueChange={(result) => row.setResult(result as Result)}
          label={t("Ergebnis")}
          items={ResultItems}
          disabled={disabled}
          fieldName={row.fieldNameFor("result")}
        />
        <ETableCell className="px-0 text-right">
          <div className="flex items-center justify-end">
            <MicrobialAssessmentDetailsDialog
              recommendation={row.assessment.recommendation}
              onRecommendationChange={row.setRecommendation}
              recommendationFieldName={row.fieldNameFor("recommendation")}
              disabled={disabled}
            ></MicrobialAssessmentDetailsDialog>
            <RowActions
              reportId={reportId}
              assessmentId={assessment.id}
              hasSample
              disabled={disabled}
            />
          </div>
        </ETableCell>
      </ETableRow>
      <AirMicrobialReferenceRow
        sample={sample}
        documentIds={[
          row.report.id,
          ...(row.report.workOrderId ? [row.report.workOrderId] : []),
        ]}
        disabled={disabled}
      />
    </>
  );
}

/**
 * AirMicrobialReferenceRow picks the set the air microbial set is compared
 * against. The candidates are the air microbial sets of the report and of
 * the work order it was created from, so the outdoor air sample of another
 * air handling unit of the work order can be picked. The row is left out
 * while there is nothing to pick.
 */
function AirMicrobialReferenceRow({
  sample,
  documentIds,
  disabled,
}: {
  sample: AirMicrobialSampleSet;
  documentIds: string[];
  disabled?: boolean;
}) {
  const reference = useAirMicrobialReference({
    set: sample,
    companyId: sample.companyId,
    documentIds,
  });

  if (!reference.hasOptions) return;

  return (
    <ETableRow>
      <ETableCell aria-hidden="true" />
      <ETableCell className="text-muted-foreground" colSpan={2}>
        {t("Vergleichsprobe")}
      </ETableCell>
      <ETableCellSelect
        colSpan={6}
        value={reference.value}
        onValueChange={reference.onValueChange}
        label={t("Vergleichsprobe")}
        items={reference.items}
        disabled={disabled}
      />
      <ETableCell aria-hidden="true" colSpan={3} />
    </ETableRow>
  );
}

function SurfaceMicrobialSampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const row = useAssessmentRow(reportId, assessment.id);

  const { data: sample } = useSamplesSurfaceMicrobialSampleSetShowQuery(
    { id: assessment.sampleId! },
    { skip: !assessment.sampleId },
  );

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateSurfaceMicrobialSampleSetCache,
    useSamplesSurfaceMicrobialSampleSetPutMutation(),
    // The sample is an entity of its own and its cells show no errors, so
    // its errors must not end up among the errors of the report.
    { writeToErrValidationFailedContext: false },
  );

  if (!row.report || !row.assessment || !sample) return;

  const disabled = row.disabled;

  return (
    <ETableRow>
      <ETableCell className="px-0">
        <AhuComponentSelect
          ahuDataId={row.report.ahuDataId!}
          componentId={row.assessment.componentId}
          onComponentIdChange={row.setComponentId}
          fieldName={row.fieldNameFor("componentId")}
          disabled={disabled}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={sample.number}
        onChange={(e) =>
          setSample(updateSurfaceMicrobialSetNumber(sample, e.target.value))
        }
        disabled={disabled}
      />
      <ETableCell className="p-0">
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => setSample({ ...sample, labId })}
          className="rounded-none border-none shadow-none"
          disabled={disabled}
        />
      </ETableCell>
      <ETableCellInput
        value={sample.unit}
        onChange={(e) => setSample({ ...sample, unit: e.target.value })}
        disabled={disabled}
      />
      <ETableCellInput
        value={sample.caso?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            caso: { ...sample.caso!, value: e.target.value },
          })
        }
        disabled={disabled}
      />
      <ETableCellInput
        value={sample.malt?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            malt: { ...sample.malt!, value: e.target.value },
          })
        }
        disabled={disabled}
      />
      <ETableCellInput
        value={sample.dg18?.value}
        onChange={(e) =>
          setSample({
            ...sample,
            dg18: { ...sample.dg18!, value: e.target.value },
          })
        }
        disabled={disabled}
      />
      <ETableCellInput
        value={sample.value}
        onChange={(e) => setSample({ ...sample, value: e.target.value })}
        disabled={disabled}
      />
      <ETableCell className="p-0">
        <MoldDifferentiationDialog
          moldDifferentiation={sample.moldDifferentiation}
          onMoldDifferentiationChange={(moldDifferentiation) =>
            setSample({ ...sample, moldDifferentiation })
          }
          disabled={disabled}
        />
      </ETableCell>
      <ETableCellDatePicker
        date={row.assessment.conductedAt}
        onDateChange={row.setConductedAt}
        disabled={disabled}
        fieldName={row.fieldNameFor("conductedAt")}
      />
      <ETableCellSelect
        value={row.assessment.result}
        onValueChange={(result) => row.setResult(result as Result)}
        label={t("Ergebnis")}
        items={ResultItems}
        disabled={disabled}
        fieldName={row.fieldNameFor("result")}
      />
      <ETableCell className="px-0 text-right">
        <div className="flex items-center justify-end">
          <MicrobialAssessmentDetailsDialog
            recommendation={row.assessment.recommendation}
            onRecommendationChange={row.setRecommendation}
            recommendationFieldName={row.fieldNameFor("recommendation")}
            disabled={disabled}
          >
            <NumberInputField
              label={t("Grenzwertig ab (Gesamtkeimzahl)")}
              number={sample.warningFrom}
              onNumberChange={(warningFrom) =>
                setSample({ ...sample, warningFrom: Math.max(0, warningFrom) })
              }
              min={0}
              disabled={disabled}
            />
            <NumberInputField
              label={t("Unzureichend über (Gesamtkeimzahl)")}
              number={sample.criticalAbove}
              onNumberChange={(criticalAbove) =>
                setSample({
                  ...sample,
                  criticalAbove: Math.max(0, criticalAbove),
                })
              }
              min={0}
              disabled={disabled}
            />
            <TextInputField
              label={t("Einheit")}
              text={sample.unit}
              onTextChange={(unit) => setSample({ ...sample, unit })}
              disabled={disabled}
            />
          </MicrobialAssessmentDetailsDialog>
          <RowActions
            reportId={reportId}
            assessmentId={assessment.id}
            hasSample
            disabled={disabled}
          />
        </div>
      </ETableCell>
    </ETableRow>
  );
}

function DustDensitySampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const row = useAssessmentRow(reportId, assessment.id);

  const { data: sample } = useSamplesDustDensitySampleShowQuery(
    { id: assessment.sampleId! },
    { skip: !assessment.sampleId },
  );

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateDustDensitySampleCache,
    useSamplesDustDensitySamplePutMutation(),
    // The sample is an entity of its own and its cells show no errors, so
    // its errors must not end up among the errors of the report.
    { writeToErrValidationFailedContext: false },
  );

  if (!row.report || !row.assessment || !sample) return;

  const disabled = row.disabled;

  return (
    <ETableRow>
      <ETableCell className="px-0">
        <AhuComponentSelect
          ahuDataId={row.report.ahuDataId!}
          componentId={row.assessment.componentId}
          onComponentIdChange={row.setComponentId}
          fieldName={row.fieldNameFor("componentId")}
          disabled={disabled}
          className="rounded-none border-none shadow-none"
        />
      </ETableCell>
      <ETableCellInput
        value={sample.number}
        onChange={(e) => setSample(updateSampleNumber(sample, e.target.value))}
        disabled={disabled}
      />
      <ETableCell className="p-0">
        <LaboratorySelect
          labId={sample.labId}
          onLabIdChange={(labId) => setSample({ ...sample, labId })}
          className="rounded-none border-none shadow-none"
          disabled={disabled}
        />
      </ETableCell>
      <ETableCellInput
        value={sample.unit}
        onChange={(e) => setSample({ ...sample, unit: e.target.value })}
        disabled={disabled}
      />

      <ETableCellInput
        value={sample.value}
        onChange={(e) => setSample({ ...sample, value: e.target.value })}
        disabled={disabled}
      />
      <ETableCell aria-hidden="true" colSpan={4} />
      <ETableCellDatePicker
        date={row.assessment.conductedAt}
        onDateChange={row.setConductedAt}
        disabled={disabled}
        fieldName={row.fieldNameFor("conductedAt")}
      />
      <ETableCellSelect
        value={row.assessment.result}
        onValueChange={(result) => row.setResult(result as Result)}
        label={t("Ergebnis")}
        items={ResultItems}
        disabled={disabled}
        fieldName={row.fieldNameFor("result")}
      />
      <ETableCell className="px-0 text-right">
        <div className="flex items-center justify-end">
          <MicrobialAssessmentDetailsDialog
            recommendation={row.assessment.recommendation}
            onRecommendationChange={row.setRecommendation}
            recommendationFieldName={row.fieldNameFor("recommendation")}
            disabled={disabled}
          >
            <TextInputField
              label={t("Richtwert")}
              text={sample.referenceValue}
              onTextChange={(referenceValue) =>
                setSample({ ...sample, referenceValue })
              }
              disabled={disabled}
            />
            <TextInputField
              label={t("Einheit")}
              text={sample.unit}
              onTextChange={(unit) => setSample({ ...sample, unit })}
              disabled={disabled}
            />
          </MicrobialAssessmentDetailsDialog>
          <RowActions
            reportId={reportId}
            assessmentId={assessment.id}
            hasSample
            disabled={disabled}
          />
        </div>
      </ETableCell>
    </ETableRow>
  );
}

function WaterSampleRow({
  reportId,
  assessment,
}: {
  reportId: UUID;
  assessment: MicrobialAssessment;
}) {
  const row = useAssessmentRow(reportId, assessment.id);

  const { data: sample } = useSamplesWaterSampleShowQuery(
    { id: assessment.sampleId! },
    { skip: !assessment.sampleId },
  );

  const [setSample] = useOptimisticDebouncedMutation(
    sample!,
    updateWaterSampleCache,
    useSamplesWaterSamplePutMutation(),
    // The sample is an entity of its own and its cells show no errors, so
    // its errors must not end up among the errors of the report.
    { writeToErrValidationFailedContext: false },
  );

  if (!row.report || !row.assessment || !sample) return;

  const disabled = row.disabled;
  const span = sample.measurements.length + 1;

  return (
    <>
      <ETableRow>
        <ETableCell className="px-0 align-top" rowSpan={span}>
          <AhuComponentSelect
            ahuDataId={row.report.ahuDataId!}
            componentId={row.assessment.componentId}
            onComponentIdChange={row.setComponentId}
            fieldName={row.fieldNameFor("componentId")}
            disabled={disabled}
            className="rounded-none border-none shadow-none"
          />
        </ETableCell>
        <ETableCell className="px-0 align-top" rowSpan={span}>
          <InputDebounced
            value={sample.number}
            onChange={(e) =>
              setSample(updateSampleNumber(sample, e.target.value))
            }
            className="border-none shadow-none focus-visible:ring-0"
            disabled={disabled}
          />
        </ETableCell>
        <ETableCell className="p-0 align-top" rowSpan={span}>
          <LaboratorySelect
            labId={sample.labId}
            onLabIdChange={(labId) => setSample({ ...sample, labId })}
            className="rounded-none border-none shadow-none"
            disabled={disabled}
          />
        </ETableCell>
        <ETableCellSelect
          label={t("Desinfiziert")}
          value={sample.desinfected ? "true" : "false"}
          onValueChange={(desinfected) =>
            setSample({ ...sample, desinfected: desinfected === "true" })
          }
          items={[
            { label: t("Ja"), value: "true" },
            { label: t("Nein"), value: "false" },
          ]}
          disabled={disabled}
        />
        <ETableCellInput
          type="number"
          value={sample.temperature}
          onChange={(e) =>
            setSample({ ...sample, temperature: Number(e.target.value) })
          }
          cellClassName="align-top"
          disabled={disabled}
        />
        <ETableCell colSpan={1}>{t("Wert")}</ETableCell>
        <ETableCell colSpan={3}>{t("Einheit")}</ETableCell>
        <ETableCellDatePicker
          date={row.assessment.conductedAt}
          onDateChange={row.setConductedAt}
          disabled={disabled}
          fieldName={row.fieldNameFor("conductedAt")}
          cellClassName="align-top"
          rowSpan={span}
        />
        <ETableCellSelect
          value={row.assessment.result}
          onValueChange={(result) => row.setResult(result as Result)}
          label={t("Ergebnis")}
          items={ResultItems}
          cellClassName="align-top"
          rowSpan={span}
          disabled={disabled}
          fieldName={row.fieldNameFor("result")}
        />
        <ETableCell className="px-0 text-right align-top" rowSpan={span}>
          <div className="flex items-center justify-end">
            <MicrobialAssessmentDetailsDialog
              recommendation={row.assessment.recommendation}
              onRecommendationChange={row.setRecommendation}
              recommendationFieldName={row.fieldNameFor("recommendation")}
              disabled={disabled}
            >
              <WaterReferenceValuesTable
                measurements={sample.measurements}
                onMeasurementChange={(m) =>
                  setSample(updateMeasurement(sample, m))
                }
                disabled={disabled}
              />
            </MicrobialAssessmentDetailsDialog>
            <RowActions
              reportId={reportId}
              assessmentId={assessment.id}
              hasSample
              disabled={disabled}
            />
          </div>
        </ETableCell>
      </ETableRow>
      {sample.measurements.map((m) => (
        <ETableRow key={m.order}>
          <ETableCellInput
            colSpan={2}
            value={m.name}
            onChange={(e) =>
              setSample(
                updateMeasurement(sample, { ...m, name: e.target.value }),
              )
            }
            disabled={disabled}
          />
          <ETableCellInput
            className="w-20"
            value={m.value}
            onChange={(e) =>
              setSample(
                updateMeasurement(sample, { ...m, value: e.target.value }),
              )
            }
            disabled={disabled}
          />
          <ETableCellInput
            colSpan={3}
            value={m.unit}
            onChange={(e) =>
              setSample(
                updateMeasurement(sample, { ...m, unit: e.target.value }),
              )
            }
            disabled={disabled}
          />
        </ETableRow>
      ))}
    </>
  );
}

/**
 * WaterReferenceValuesTable edits the reference values and units of the
 * measurements of a water sample. The measurements themselves are fixed
 * when the sample is created.
 */
function WaterReferenceValuesTable({
  measurements,
  onMeasurementChange,
  disabled,
}: {
  measurements: Measurement[];
  onMeasurementChange: (measurement: Measurement) => void;
  disabled?: boolean;
}) {
  return (
    <ETable>
      <ETableHeader>
        <ETableRow>
          <ETableHead>{t("Messung")}</ETableHead>
          <ETableHead className="w-28">{t("Richtwert")}</ETableHead>
          <ETableHead className="w-32">{t("Einheit")}</ETableHead>
        </ETableRow>
      </ETableHeader>
      <ETableBody>
        {measurements.map((m) => (
          <ETableRow key={m.order}>
            <ETableCell>{m.name}</ETableCell>
            <ETableCellInput
              value={m.referenceValue}
              onChange={(e) =>
                onMeasurementChange({ ...m, referenceValue: e.target.value })
              }
              disabled={disabled}
            />
            <ETableCellInput
              value={m.unit}
              onChange={(e) =>
                onMeasurementChange({ ...m, unit: e.target.value })
              }
              disabled={disabled}
            />
          </ETableRow>
        ))}
      </ETableBody>
    </ETable>
  );
}
